import { distanceTripsQuerySchema, type DistanceTripsQuery, type Page } from '@chukua/common';
import { useEffect, useMemo, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { ApiError } from '../../../shared/shared.api';
import { listDistanceTrips, type DistanceTripItem } from '../distance-agreement.api';
import type { CloseOutTab } from '../distance-agreement.paths';
import { TAB_STATES } from '../policies/distance-labels.policy';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type CloseOutTripsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<DistanceTripItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error'; error: unknown };

const keyOf = (query: DistanceTripsQuery) => toListParams(query).toString();

/** The page of closed-out trips the URL's list query and the tab ask for, re-fetched whenever either changes. The tab fixes the state; the rest stays the URL's. */
export function useCloseOutTrips(tab: CloseOutTab) {
  const list = useListQuery(distanceTripsQuerySchema);
  const tabState = TAB_STATES[tab];
  const query = useMemo<DistanceTripsQuery>(() => (tabState ? { ...list.query, state: tabState } : list.query), [list.query, tabState]);
  const [state, setState] = useState<CloseOutTripsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listDistanceTrips(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((error) => {
        if (!cancelled) setState(error instanceof ApiError && error.status === 403 ? { status: 'forbidden' } : { status: 'error', error });
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);
  return { state, list, isRefreshing };
}
